import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPlus, FaTrash, FaMoneyBill, FaUsers, FaCalendar } from 'react-icons/fa';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import OrdersModal from '../../components/modals/OrdersModal';
import {
  C2BOrderDto,
  PaymentOrderStatus,
  ProductDto,
} from '../../../services/model/marketplace.model';
import { getOrders, deleteOrder } from '../../../services/orders.marketplace.service';
import { getProducts } from '../../../services/products.marketplace.service';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';

export default function MarketplaceOrdersPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.orders' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');

  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.B2C_CONNECTION,
  );

  const [orders, setOrders] = useState<C2BOrderDto[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);

  // Modal states
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<C2BOrderDto | null>(null);

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load orders
      const fetchedOrders = await getOrders(productId || '');
      setOrders(fetchedOrders.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));

      // Load products to get product names
      const fetchedProducts = await getProducts();
      setProducts(fetchedProducts);

      // If productId is provided, find the product
      if (productId) {
        const product = fetchedProducts.find((p) => p.id === productId);
        setSelectedProduct(product || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      await deleteOrder(orderToDelete.c2bOrderId);
      setOrders(orders.filter((o) => o.c2bOrderId !== orderToDelete.c2bOrderId));
      setOrderToDelete(null);
      setDeleteModalOpen(false);
      toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const openDeleteModal = (order: C2BOrderDto) => {
    setOrderToDelete(order);
    setDeleteModalOpen(true);
  };

  const openNewPaymentRequestModal = () => {
    if (!selectedProduct) {
      toast.error('No product selected');
      return;
    }
    setOrdersModalOpen(true);
  };

  const handleBackToProducts = () => {
    navigate('/financial/sales-collect', { state: { activeTab: 'sales' } });
  };

  const getStatusBadge = (status: PaymentOrderStatus) => {
    const statusConfig = {
      [PaymentOrderStatus.PENDING]: {
        label: t('pending'),
        className: 'bg-orange-100 text-secondary',
      },
      [PaymentOrderStatus.PAID]: { label: t('paid'), className: 'bg-green-100 text-success' },
      [PaymentOrderStatus.AUTHORIZED]: { label: t('authorized'), className: 'bg-green-100 text-success' },
      [PaymentOrderStatus.CANCELLED]: {
        label: t('cancelled'),
        className: 'bg-red-100 text-danger',
      },
      [PaymentOrderStatus.FAILED]: { label: t('failed'), className: 'bg-red-100 text-danger' },
      [PaymentOrderStatus.REFUNDED]: { label: t('refunded'), className: 'bg-purple-100 text-purple-800' },
      [PaymentOrderStatus.TO_BE_REFUNDED]: { label: t('toBeRefunded'), className: 'bg-purple-60 text-purple-600' },
    };

    const config = statusConfig[status] || {
      label: 'Unknown',
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(date));
  };

  // Filter orders by product if productId is provided
  const filteredOrders = productId
    ? orders.filter((order) => {
        const product = products.find((p) => p.id === order.productId);
        return product && product.id === productId;
      })
    : orders;


  // Calculate tile data for selected product
  const tileData = React.useMemo(() => {
    if (!selectedProduct || !filteredOrders.length) return null;

    const totalOrders = filteredOrders.length;
    const paidOrders = filteredOrders.filter((order) => order.status === PaymentOrderStatus.PAID);
    const totalCollected = paidOrders.reduce((sum, order) => sum + order.amount, 0);
    const pendingOrders = filteredOrders.filter(
      (order) => (order.status === PaymentOrderStatus.PENDING),
    );


    return {
      productPrice: selectedProduct.price,
      totalOrders,
      pendingOrders: pendingOrders.length,
      amountCollected: totalCollected,
    };
  }, [selectedProduct, filteredOrders]);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <Button variant="tertiary" onClick={handleBackToProducts} className="mr-4">
          <FaArrowLeft className="mr-2 h-4 w-4" />
          {t('backToProducts')}
        </Button>
      </div>

      {/* Page Header */}
      <PageHeader
        title={selectedProduct?.name || t('title')}
        subtitle={selectedProduct?.description || t('subtitle')}
        primaryButton={
          selectedProduct ? (
            <PrimaryButton text={t('newPaymentRequest')} onClick={openNewPaymentRequestModal} />
          ) : undefined
        }
      />

      {/* Stats Tiles */}
      {tileData && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Product Price Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaMoneyBill className="text-primary-600 h-8 w-8" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Product Price</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.productPrice, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Total Orders Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaUsers className="h-8 w-8 text-primary-300" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{tileData.totalOrders}</p>
              </div>
            </div>
          </div>

          {/* Pending Orders Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCalendar className="h-8 w-8 text-secondary" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Pending Orders</p>
                <p className="text-2xl font-bold text-gray-900">{tileData.pendingOrders}</p>
              </div>
            </div>
          </div>

          {/* Amount Collected Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaMoneyBill className="h-8 w-8 text-success" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Amount Collected</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.amountCollected, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-lg text-gray-500">Loading orders...</div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-gray-500">
            {selectedProduct
              ? `No payment requests for "${selectedProduct.name}" yet`
              : 'No payment requests yet'}
          </div>
        </div>
      ) : (
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <HeaderCell>{t('to')}</HeaderCell>
              {!selectedProduct && <HeaderCell>Product</HeaderCell>}
              <HeaderCell>{t('requestDate')}</HeaderCell>
              <HeaderCell>{t('status')}</HeaderCell>
              <HeaderCell>{t('amountPaid')}</HeaderCell>
              <HeaderCell>{t('paymentDate')}</HeaderCell>
              <HeaderCell>{t('actions')}</HeaderCell>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            <BaseTableSpacerRow />
            {filteredOrders.map((order) => {
              const product = products.find((p) => p.id === order.productId);
              return (
                <BaseTableDataRow key={order.c2bOrderId}>
                  <DataCell>
                    <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                  </DataCell>
                  {!selectedProduct && (
                    <DataCell>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {product?.name || 'Unknown Product'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatEuroCents(order.amount, i18n.language)}
                        </div>
                      </div>
                    </DataCell>
                  )}
                  <DataCell>{formatDate(order.createdAt)}</DataCell>
                  <DataCell>{getStatusBadge(order.status)}</DataCell>
                  <DataCell>
                    {(order.status !== PaymentOrderStatus.PENDING && order.status !== PaymentOrderStatus.CANCELLED)
                      ? formatEuroCents(order.amount, i18n.language)
                      : '-'}
                  </DataCell>
                  <DataCell>
                    {(order.status !== PaymentOrderStatus.PENDING && order.status !== PaymentOrderStatus.CANCELLED) ? formatDate(order.updatedAt) : '-'}
                  </DataCell>
                  <DataCell>
                    <ActionMenu>
                      {order.status === PaymentOrderStatus.PENDING && (
                        <ActionMenuButton
                          icon={FaTrash}
                          text={t('delete')}
                          className="text-danger"
                          onClick={() => openDeleteModal(order)}
                        />
                      ) }
                    </ActionMenu>
                  </DataCell>
                </BaseTableDataRow>
              );
            })}
          </BaseTableBody>
        </BaseTable>
      )}

      {/* Orders Modal */}
      {selectedProduct && (
        <OrdersModal
          open={ordersModalOpen}
          setOpen={setOrdersModalOpen}
          productId={selectedProduct.id}
          productName={selectedProduct.name}
          members={connections}
        />
      )}

      {/* Delete Confirmation Modal */}
      <AreYouSureModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete Payment Request"
        description={`Are you sure you want to delete this payment request for "${orderToDelete?.customerName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteOrder}
        saveClassName="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}
